import { useRef } from "react";
import { motion, useInView, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Bot, Mic, FlaskConical, Building2, Layers, Lightbulb, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SkeletonCard } from "@/components/SkeletonShimmer";

/* ── Platform service definitions ── */
const platforms = [
  {
    icon: Bot,
    name: "Agentic Automation Platform",
    tagline: "Automate Everything. Code Nothing.",
    valueProp:
      "A unified zero-code platform deploying autonomous agents, conversational workflows, and document AI — so your team focuses on outcomes, not operations.",
    bullets: [
      "Zero-code deployment for complex multi-step workflows",
      "Autonomous agents running 24/7 without supervision",
      "Native document intelligence & data extraction",
      "Interactive avatar interfaces for customer-facing experiences",
    ],
    stat: "10× faster than custom dev",
    accent: "indigo",
    featured: true,
  },
  {
    icon: Mic,
    name: "Voice AI & Sales Intelligence",
    tagline: "Your Sales Team, Always On.",
    valueProp:
      "AI voice agents that qualify leads, nurture prospects, and hand off high-intent opportunities at exactly the right moment.",
    bullets: [
      "Multi-agent call orchestration",
      "Real-time lead qualification & CRM sync",
    ],
    stat: "3× more qualified leads",
    accent: "violet",
    featured: false,
  },
  {
    icon: FlaskConical,
    name: "Deep Tech Research Intelligence",
    tagline: "Real-Time Intelligence, Delivered Hourly.",
    valueProp:
      "AI research engine monitoring trusted sources and delivering benchmarked, domain-specific intelligence to accelerate R&D.",
    bullets: [
      "1,000+ verified technical sources monitored",
      "Hourly summaries with accuracy benchmarking",
    ],
    stat: "35% faster R&D workflows",
    accent: "amber",
    featured: false,
  },
  {
    icon: Building2,
    name: "Intelligent Real Estate Technology",
    tagline: "Where Property Meets Intelligence.",
    valueProp:
      "End-to-end proptech intelligence — AI property search, live market analytics, and automated buyer/seller workflows.",
    bullets: [
      "AI property matching with neighborhood insights",
      "Automated buyer/seller journey & pre-approval",
    ],
    stat: "2× faster deal cycles",
    accent: "emerald",
    featured: false,
  },
  {
    icon: Layers,
    name: "Full-Stack AI Engineering",
    tagline: "From MVP to Market, Faster.",
    valueProp:
      "End-to-end product engineering for pharma, fintech, and enterprise — from architecture through cloud-native deployment.",
    bullets: [
      "Industry-vertical solutions built for your domain",
      "Cloud-native architecture with IoT integration",
    ],
    stat: "Weeks, not months",
    accent: "cyan",
    featured: false,
  },
  {
    icon: Lightbulb,
    name: "Strategy & Consulting",
    tagline: "Clarity Before Code.",
    valueProp:
      "We map your operations, identify high-leverage AI opportunities, and build a roadmap you can act on — before a single line of code is written.",
    bullets: [
      "AI readiness assessment & gap analysis",
      "PoC development to validate ROI before full investment",
      "Vendor-neutral technology selection",
      "Ongoing advisory to stay ahead of the curve",
    ],
    stat: "100% aligned to your goals",
    accent: "rose",
    featured: true,
  },
];

/* ── Accent style map ── */
const accentMap = {
  indigo: {
    border: "border-indigo-500/25",
    glow: "shadow-indigo-500/10",
    icon: "text-indigo-400",
    iconBg: "bg-indigo-500/10",
    tagline: "text-indigo-400",
    dot: "bg-indigo-400",
    stat: "bg-indigo-500/15 text-indigo-300 border-indigo-500/20",
    shimmer: "from-indigo-500/0 via-indigo-500/5 to-indigo-500/0",
  },
  violet: {
    border: "border-violet-500/25",
    glow: "shadow-violet-500/10",
    icon: "text-violet-400",
    iconBg: "bg-violet-500/10",
    tagline: "text-violet-400",
    dot: "bg-violet-400",
    stat: "bg-violet-500/15 text-violet-300 border-violet-500/20",
    shimmer: "from-violet-500/0 via-violet-500/5 to-violet-500/0",
  },
  amber: {
    border: "border-amber-500/25",
    glow: "shadow-amber-500/10",
    icon: "text-amber-400",
    iconBg: "bg-amber-500/10",
    tagline: "text-amber-400",
    dot: "bg-amber-400",
    stat: "bg-amber-500/15 text-amber-300 border-amber-500/20",
    shimmer: "from-amber-500/0 via-amber-500/5 to-amber-500/0",
  },
  emerald: {
    border: "border-emerald-500/25",
    glow: "shadow-emerald-500/10",
    icon: "text-emerald-400",
    iconBg: "bg-emerald-500/10",
    tagline: "text-emerald-400",
    dot: "bg-emerald-400",
    stat: "bg-emerald-500/15 text-emerald-300 border-emerald-500/20",
    shimmer: "from-emerald-500/0 via-emerald-500/5 to-emerald-500/0",
  },
  cyan: {
    border: "border-cyan-500/25",
    glow: "shadow-cyan-500/10",
    icon: "text-cyan-400",
    iconBg: "bg-cyan-500/10",
    tagline: "text-cyan-400",
    dot: "bg-cyan-400",
    stat: "bg-cyan-500/15 text-cyan-300 border-cyan-500/20",
    shimmer: "from-cyan-500/0 via-cyan-500/5 to-cyan-500/0",
  },
  rose: {
    border: "border-rose-500/25",
    glow: "shadow-rose-500/10",
    icon: "text-rose-400",
    iconBg: "bg-rose-500/10",
    tagline: "text-rose-400",
    dot: "bg-rose-400",
    stat: "bg-rose-500/15 text-rose-300 border-rose-500/20",
    shimmer: "from-rose-500/0 via-rose-500/5 to-rose-500/0",
  },
};

/* ── Individual platform card ── */
function PlatformCard({ platform, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  const a = accentMap[platform.accent] || accentMap.indigo;
  const Icon = platform.icon;

  /* 3D tilt */
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [5, -5]), { stiffness: 200, damping: 20 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-5, 5]), { stiffness: 200, damping: 20 });

  const handleMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mouseX.set((e.clientX - r.left) / r.width - 0.5);
    mouseY.set((e.clientY - r.top) / r.height - 0.5);
  };
  const resetMouse = () => { mouseX.set(0); mouseY.set(0); };

  const colSpan = platform.featured ? "md:col-span-2" : "md:col-span-1";

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 48, scale: 0.96 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ delay: index * 0.09, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className={colSpan}
      style={{ perspective: 900 }}
      data-testid={`platform-card-${platform.name}`}
    >
      <motion.div
        onMouseMove={handleMouse}
        onMouseLeave={resetMouse}
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className={`
          relative h-full rounded-2xl border ${a.border}
          bg-white/[0.03] backdrop-blur-xl
          hover:shadow-2xl ${a.glow}
          transition-shadow duration-500 group cursor-default overflow-hidden
        `}
      >
        {/* Glassmorphic base */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/[0.05] to-transparent pointer-events-none" />

        {/* Hover shimmer scan */}
        <div
          className={`absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r ${a.shimmer} pointer-events-none transition-none`}
          style={{ transition: "transform 0.8s ease" }}
        />

        <div
          className={`relative h-full flex flex-col gap-5 ${
            platform.featured ? "p-8 md:p-10" : "p-7 md:p-8"
          }`}
        >
          {/* Top row: icon + stat badge */}
          <div className="flex items-start justify-between gap-4">
            <div
              className={`${platform.featured ? "w-14 h-14" : "w-12 h-12"} rounded-xl ${a.iconBg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}
            >
              <Icon size={platform.featured ? 28 : 22} className={a.icon} />
            </div>
            <span
              className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${a.stat} whitespace-nowrap`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${a.dot} animate-pulse flex-shrink-0`} />
              {platform.stat}
            </span>
          </div>

          {/* Name + tagline */}
          <div>
            <h3 className={`font-heading font-bold text-white leading-tight mb-1.5 ${platform.featured ? "text-xl md:text-2xl" : "text-lg"}`}>
              {platform.name}
            </h3>
            <p className={`text-sm font-semibold ${a.tagline}`}>{platform.tagline}</p>
          </div>

          {/* Value prop */}
          <p className={`text-slate-400 leading-relaxed flex-1 ${platform.featured ? "text-base" : "text-sm"}`}>
            {platform.valueProp}
          </p>

          {/* Bullets */}
          <ul className="space-y-2">
            {platform.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2.5">
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${a.dot} flex-shrink-0`} />
                <span className="text-sm text-slate-400 leading-snug">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Main BentoDashboard ── */
export default function BentoDashboard({ loading = false }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <section
      ref={ref}
      className="relative py-24 md:py-36 px-6 md:px-12"
      data-testid="bento-dashboard"
    >
      {/* Ambient background orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-indigo-600/[0.05] rounded-full blur-[140px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-rose-500/[0.04] rounded-full blur-[120px]" />
      </div>

      {/* Section header */}
      <div className="max-w-7xl mx-auto mb-16 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 text-indigo-400 text-xs font-semibold tracking-wider uppercase mb-4">
            <span className="w-6 h-px bg-indigo-500/50" />
            The brgt.ai Platform
            <span className="w-6 h-px bg-indigo-500/50" />
          </span>
          <h2
            className="font-heading font-bold text-white leading-tight mb-4"
            style={{ fontSize: "clamp(1.8rem, 3vw + 0.5rem, 3rem)" }}
          >
            One Platform.{" "}
            <span className="text-indigo-400">Every AI Capability You Need.</span>
          </h2>
          <p className="text-slate-400 text-base md:text-lg max-w-2xl leading-relaxed">
            brgt.ai isn't a consultancy that outsources your stack. We build,
            own, and deploy the technology ourselves — a unified, accountable
            partner from strategy to scale.
          </p>
        </motion.div>
      </div>

      {/* Platform card grid */}
      <div className="max-w-7xl mx-auto relative">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(6)].map((_, i) => (
              <SkeletonCard key={i} className={i === 0 || i === 5 ? "md:col-span-2" : ""} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-auto">
            {platforms.map((p, i) => (
              <PlatformCard key={p.name} platform={p} index={i} />
            ))}
          </div>
        )}
      </div>

      {/* CTAs */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.65, duration: 0.5 }}
        className="max-w-7xl mx-auto mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 relative"
      >
        <Link
          to="/solutions"
          className="group inline-flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-500 rounded-full px-7 py-3.5 font-semibold text-sm transition-all shadow-lg shadow-indigo-600/25 active:scale-95"
        >
          Explore the Platform
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
        </Link>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 border border-white/15 text-slate-300 hover:text-white hover:border-white/30 rounded-full px-7 py-3.5 font-semibold text-sm transition-all active:scale-95"
        >
          Talk to an Expert
        </Link>
      </motion.div>
    </section>
  );
}
